/**
 * Edge Function Client
 *
 * Frontend helper to call Supabase Edge Functions.
 * Handles JWT authentication, error handling, and 401 redirects.
 *
 * Replaces direct service role client usage in the browser.
 */

import { getStudentToken } from '@utils/session-helper';
import { getSupabaseClient } from './supabase-client';

/**
 * Call a Supabase Edge Function
 *
 * @param functionName - Edge Function name (e.g., 'student-auth')
 * @param action - Action to perform (e.g., 'login')
 * @param params - Additional parameters for the action
 * @param requireAuth - Whether to include JWT (default: true)
 * @returns Parsed JSON response
 */
export async function callEdgeFunction<T>(
  functionName: string,
  action: string,
  params: Record<string, unknown> = {},
  requireAuth = true
): Promise<T> {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Build request body — include student JWT token in body (not headers)
  // to avoid Supabase gateway stripping custom headers
  const body: Record<string, unknown> = { action, ...params };

  if (requireAuth) {
    const token = getStudentToken();
    if (!token) {
      // No token — throw without redirect. Student pages already gate entry via
      // requireStudentAuth(); teacher/guardian pages must not be redirected to student auth.
      throw new Error('No student session token');
    }
    body._token = token;
  }

  const response = await fetch(
    `${supabaseUrl}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
  );

  if (response.status === 401) {
    // JWT rejected by edge function — token expired mid-session, redirect to re-authenticate
    window.location.href = '/auth/auth.html?type=student';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorData.error || `Edge function error: ${response.status}`);
  }

  return response.json();
}

/**
 * Call a Supabase Edge Function using the guardian's Supabase Auth session token.
 *
 * Sends `_guardian_token` (Supabase Auth JWT) and `student_id` in the request body.
 * The Edge Function verifies the guardian owns the student before returning data.
 *
 * On 401, redirects to the guardian login page (not the student login).
 *
 * @param functionName - Edge Function name (e.g., 'student-kpi')
 * @param action - Action to perform (e.g., 'get-explanations')
 * @param params - Parameters including required `student_id`
 * @returns Parsed JSON response
 */
export async function callGuardianEdgeFunction<T>(
  functionName: string,
  action: string,
  params: Record<string, unknown> & { student_id: string }
): Promise<T> {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }

  const supabase = getSupabaseClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;

  if (!session?.access_token) {
    // No guardian session — redirect to guardian login
    window.location.href = '/auth/auth.html';
    throw new Error('No guardian session');
  }

  const body: Record<string, unknown> = {
    action,
    _guardian_token: session.access_token,
    ...params,
  };

  const response = await fetch(
    `${supabaseUrl}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );

  if (response.status === 401) {
    // Guardian JWT rejected — session expired, redirect to guardian login
    window.location.href = '/auth/auth.html';
    throw new Error('Guardian session expired');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorData.error || `Edge function error: ${response.status}`);
  }

  return response.json();
}
